import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiResult } from 'src/app/shared/models/api-result.model';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { IAccount } from '../../shared/interfaces/accounts/IAccount';
import { IEmployeeSCDItem } from '../../shared/interfaces/IEmployeeSCDItem';
import { IOrgUnit } from '../../shared/interfaces/orgunit/IOrgUnit';
import { IAccountType } from '../../shared/interfaces/accounts/IAccountType';
import { validateDateChronology } from '../../shared/validators/date-chronology.validator';
import { AccountTypeEnum } from '../../shared/enums/AccountType.enum';
import { INotification } from '../../shared/interfaces/INotification';
import { NotificationService } from '../../shared/services/notification.service';
import { IConfirmationDialogData } from '../../shared/interfaces/IConfirmationDialogData';
import { DateService } from '../../shared/services/date.service';
import { IAccountsAggregationProps, IAccountsPaginationProps } from '../../shared/interfaces/accounts/IAccountsPaginationProps';
import { IFilter } from '../../shared/interfaces/IFilter';
import { COMPARE_OPERATOR } from '../../shared/enums/CompareOperator.enum';

export class AccountsOperations {
  public static readonly accountTypes: IAccountType[] = [
    { title: AccountTypeEnum.BUDGET, translationKey: 'account.budget' },
    { title: AccountTypeEnum.ADDITIONAL_ORDER, translationKey: 'account.accountTypes.Zusatzauftrag' },
    { title: AccountTypeEnum.GK_INTERN, translationKey: 'account.accountTypes.GKintern' },
    { title: AccountTypeEnum.ACQUISITION, translationKey: 'account.accountTypes.Zukauf' },
  ];
  public static readonly accountAreas: number[] = [0, 1100];

  public static createNewAccount(accountingMonth: Date): IAccount {
    const lastDayOfCurrentFiscalYear = this.getLastDayOfFiscalYear();
    return {
      id: 0,
      accountInternalPSP: '',
      accountName: null,
      accountNumber: null,
      accountOwner: null,
      accountPreviousFiscalYearID: null,
      accountType: null,
      accountingArea: null,
      accountingInformation: null,
      actualBudget: null,
      bgbk: '',
      billingPSPBZPO: '',
      budget: null,
      budgetPlaning: null,
      budgetPlaningComment: null,
      chargingKeyTechnology: null,
      chargingKeyTechnologyPlanning: null,
      comment: null,
      confirmedBudget: null,
      customer: null,
      customerOrder: '',
      hourlyRate: null,
      load: null,
      loadTravelExpenses: null,
      offerNumber: null,
      orderDate: null,
      orderHPosRK: '',
      orderHPosOC: '',
      orderMainPos: '',
      orderer: null,
      orgaUnit: null,
      partner: null,
      partnerGroups: null,
      partnerGroup: null,
      projectOrgID: '',
      sapSDOrder: null,
      toGeneralLedgerAccount: '',
      totalCosts: 0,
      totalCostsWithoutPreview: 0,
      totalCostsOnlyPreview: 0,
      tpa: null,
      vIst: null,
      validFrom: accountingMonth,
      validTo: lastDayOfCurrentFiscalYear,
      projectAssigned: null,
      client: null,
      orderNumberNPLKST: null,
      startDate: null,
      endDate: null,
      teamName: null,
      groupName: null,
      allocationTravelPercentage: null,
      allocationPercentage: null,
      settlementWbs: null,
      disabled: true,
    };
  }

  public static setBudgetValidators(accountType: string, accountInfoForm: FormGroup, isNewAccount: boolean): void {
    if (accountType === AccountTypeEnum.ADDITIONAL_ORDER) {
      accountInfoForm.get('budget').setValidators([]);
      accountInfoForm.get('budget').updateValueAndValidity();
      accountInfoForm.get('confirmedBudget').setValidators([]);
      accountInfoForm.get('confirmedBudget').updateValueAndValidity();
      if (isNewAccount) {
        accountInfoForm.get('budget').patchValue(0);
        accountInfoForm.get('confirmedBudget').patchValue(0);
      }
    } else if (accountType === AccountTypeEnum.GK_INTERN || accountType === AccountTypeEnum.ACQUISITION) {
      accountInfoForm.get('budget').setValidators([]);
      accountInfoForm.get('budget').updateValueAndValidity();
      accountInfoForm.get('confirmedBudget').setValidators([]);
      accountInfoForm.get('confirmedBudget').updateValueAndValidity();
      if (isNewAccount) {
        accountInfoForm.get('budget').reset();
        accountInfoForm.get('confirmedBudget').reset();
      }
    } else {
      accountInfoForm.get('budget').setValidators([Validators.required]);
      accountInfoForm.get('budget').updateValueAndValidity();
      accountInfoForm.get('confirmedBudget').setValidators([Validators.required]);
      accountInfoForm.get('confirmedBudget').updateValueAndValidity();
      if (isNewAccount) {
        accountInfoForm.get('budget').reset();
        accountInfoForm.get('confirmedBudget').reset();
      }
    }
  }

  public static initializeOrgUnits(orgUnits: IOrgUnit[]): IOrgUnit[] {
    const uniqueOrgUnits: IOrgUnit[] = [];
    orgUnits.forEach(orgUnit => {
      if (!uniqueOrgUnits.map(uniqueOrgUnit => uniqueOrgUnit.groupName).includes(orgUnit.groupName)) {
        uniqueOrgUnits.push(orgUnit);
      }
    });
    return uniqueOrgUnits;
  }

  public static showRequiredColumnsMessage(notificationService: NotificationService): void {
    const notification: INotification = {
      type: 'Error',
      title: 'navigation.accounts',
      desc: 'admin.editHolidays.requiredColumns',
    };
    notificationService.showMessage(notification);
  }

  public static showInvalidDatesMessage(notificationService: NotificationService, translationKey: string): void {
    const notification: INotification = {
      type: 'Error',
      title: `navigation.${translationKey}`,
      desc: 'admin.editHolidays.invalidDates',
    };
    notificationService.showMessage(notification);
  }

  public static getWaringDialogData(): IConfirmationDialogData {
    return {
      title: 'default.areYouSure',
      desc: 'account.accountExceedsFY',
      confirmationBtnText: 'default.confirm',
      isWarning: true,
    };
  }

  public static showConfirmationMessage(notificationService: NotificationService, isDelete = false): void {
    const notification: INotification = {
      type: 'Success',
      title: 'navigation.accounts',
      desc: isDelete ? 'account.accountDeleted' : 'account.accountSaved',
    };
    notificationService.showMessage(notification);
  }

  public static showErrorMessage(notificationService: NotificationService): Observable<null> {
    const notification: INotification = {
      type: 'Error',
      title: 'navigation.accounts',
      desc: 'default.dialog.save.error',
    };
    notificationService.showMessage(notification);
    return of(null);
  }

  public static showConfirmationMessageMultiple(notificationService: NotificationService): void {
    const notification: INotification = {
      type: 'Success',
      title: 'navigation.accounts',
      desc: 'account.accountSavedMultiple',
    };
    notificationService.showMessage(notification);
  }

  public static showErrorMessageMultiple(notificationService: NotificationService): Observable<null> {
    const notification: INotification = {
      type: 'Error',
      title: 'navigation.accounts',
      desc: 'account.accountSavedMultipleError',
    };
    notificationService.showMessage(notification);
    return of(null);
  }

  public static showEmailConfirmationMessage(notificationService: NotificationService): void {
    const notification: INotification = {
      type: 'Success',
      title: 'navigation.accounts',
      desc: 'account.emailSentSuccess',
    };
    notificationService.showMessage(notification);
  }

  public static showEmailErrorMessage(notificationService: NotificationService): Observable<null> {
    const notification: INotification = {
      type: 'Error',
      title: 'navigation.accounts',
      desc: 'account.emailSentError',
    };
    notificationService.showMessage(notification);
    return of(null);
  }

  public static showNotFoundMessage(notificationService: NotificationService): void {
    const notification: INotification = {
      type: 'Error',
      title: 'navigation.accounts',
      desc: 'account.notFound',
    };
    notificationService.showMessage(notification);
  }

  public static showDeletionErrorMessage(notificationService: NotificationService): void {
    const notification: INotification = {
      type: 'Error',
      title: 'navigation.accounts',
      desc: 'account.accountDeleteError',
    };
    notificationService.showMessage(notification);
  }

  public static showDeletionNotAllowedMessage(notificationService: NotificationService): void {
    const notification: INotification = {
      type: 'Error',
      title: 'navigation.accounts',
      desc: 'account.accountDeleteHasCosts',
    };
    notificationService.showMessage(notification);
  }

  public static showClipboardConfirmationMessage(notificationService: NotificationService): void {
    const notification: INotification = {
      type: 'Success',
      title: 'navigation.accounts',
      desc: 'account.accountValueCopied',
    };
    notificationService.showMessage(notification);
  }

  public static buildForm(account: IAccount, isEditMode: boolean): FormGroup {
    return new FormGroup(
      {
        id: new FormControl<number>({
          value: account.id,
          disabled: true,
        }),
        accountNumber: new FormControl<number>({
          value: account.accountNumber,
          disabled: true,
        }),
        accountName: new FormControl<string>(
          {
            value: account.accountName,
            disabled: !isEditMode,
          },
          Validators.required,
        ),
        accountOwner: new FormControl<IEmployeeSCDItem>(
          {
            value: account.accountOwner,
            disabled: !isEditMode,
          },
          Validators.required,
        ),
        budget: new FormControl<number>(
          {
            value: account.budget,
            disabled: !isEditMode,
          },
          [Validators.required],
        ),
        confirmedBudget: new FormControl<number>(
          {
            value: account.confirmedBudget,
            disabled: !isEditMode,
          },
          Validators.required,
        ),
        actualBudget: new FormControl<number>({
          value: account.actualBudget,
          disabled: !isEditMode,
        }),
        customer: new FormControl<string>({
          value: account.customer,
          disabled: !isEditMode,
        }),
        orgaUnit: new FormControl<IOrgUnit>(
          {
            value: account.orgaUnit,
            disabled: !isEditMode,
          },
          Validators.required,
        ),
        vIst: new FormControl<number>({
          value: account.vIst,
          disabled: !isEditMode,
        }),
        budgetPlaning: new FormControl<number>({
          value: account.budgetPlaning,
          disabled: !isEditMode,
        }),
        budgetPlaningComment: new FormControl<string>({
          value: account.budgetPlaningComment,
          disabled: !isEditMode,
        }),
        accountType: new FormControl<string>(
          {
            value: account.accountType,
            disabled: !isEditMode,
          },
          Validators.required,
        ),
        partner: new FormControl<string>(
          {
            value: account.partner,
            disabled: !isEditMode,
          },
          Validators.required,
        ),
        partnerGroup: new FormControl<string>(
          {
            value: account.partnerGroups,
            disabled: !isEditMode,
          },
          Validators.required,
        ),
        offerNumber: new FormControl<string>({
          value: account.offerNumber,
          disabled: !isEditMode,
        }),
        validFrom: new FormControl<Date>(
          {
            value: new Date(account.validFrom),
            disabled: !isEditMode,
          },
          Validators.required,
        ),
        validTo: new FormControl<Date>(
          {
            value: new Date(account.validTo),
            disabled: !isEditMode,
          },
          Validators.required,
        ),
        tpa: new FormControl<string>({
          value: account.tpa,
          disabled: !isEditMode,
        }),
        hourlyRate: new FormControl<number>(
          {
            value: account.hourlyRate,
            disabled: !isEditMode,
          },
          Validators.required,
        ),
        comment: new FormControl<string>({
          value: account.comment,
          disabled: !isEditMode,
        }),
        projectOrgID: new FormControl<string>({
          value: account.projectOrgID,
          disabled: !isEditMode,
        }),
        bgbk: new FormControl<string>({
          value: account.bgbk,
          disabled: !isEditMode,
        }),
        orderDate: new FormControl<Date>({
          value: account.orderDate,
          disabled: !isEditMode,
        }),
        customerOrder: new FormControl<string>({
          value: account.customerOrder,
          disabled: !isEditMode,
        }),
        orderMainPos: new FormControl<string>({
          value: account.orderMainPos,
          disabled: !isEditMode,
        }),
        orderHPosRK: new FormControl<string>({
          value: account.orderHPosRK,
          disabled: !isEditMode,
        }),
        orderHPosOC: new FormControl<string>({
          value: account.orderHPosOC,
          disabled: !isEditMode,
        }),
        sapSDOrder: new FormControl<number>({
          value: account.sapSDOrder,
          disabled: !isEditMode,
        }),
        billingPSPBZPO: new FormControl<string>({
          value: account.billingPSPBZPO,
          disabled: !isEditMode,
        }),
        toGeneralLedgerAccount: new FormControl<string>({
          value: account.toGeneralLedgerAccount,
          disabled: !isEditMode,
        }),
        accountingArea: new FormControl<number>(
          {
            value: account.accountingArea,
            disabled: !isEditMode,
          },
          [Validators.required, Validators.pattern('^[0-9]*$')],
        ),
        orderer: new FormControl<string>({
          value: account.orderer,
          disabled: !isEditMode,
        }),
        accountInternalPSP: new FormControl<string>({
          value: account.accountInternalPSP,
          disabled: !isEditMode,
        }),
      },
      { validators: validateDateChronology('validFrom', 'validTo') },
    );
  }

  public static getAccountIdFromApiResult(apiResult: ApiResult | HttpErrorResponse): number {
    if (apiResult instanceof HttpErrorResponse) {
      return null;
    }
    const match = apiResult.message.match(/AccountId=\s*(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  public static getAccountNumberFromApiResult(apiResult: ApiResult | HttpErrorResponse): number {
    if (apiResult instanceof HttpErrorResponse) {
      return null;
    }
    const match = apiResult.message.match(/AccountNumber=\s*(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  public static determineDeletionWarningCondition(account: IAccount, dateService: DateService): boolean {
    const newFiscalYearDate = dateService.getBusinessYearRange(new Date())[0];
    const activeTechnologyKeys = account.accountingInformation.technologyKeys.filter(
      technologyKey => new Date(technologyKey.assignedTeam.inactive) > newFiscalYearDate || technologyKey.partialBudget > 0,
    );
    let warning = false;
    if (activeTechnologyKeys.length) {
      activeTechnologyKeys.forEach(activeTechnologyKey => {
        if (activeTechnologyKey.costs && activeTechnologyKey.costs.costs && activeTechnologyKey.costs.costs > 0) {
          warning = true;
        }
      });
    }
    return warning;
  }

  public static buildAccountsPaginationProps(
    fiscalYear: string,
    pageSize: number,
    filters: IFilter[],
    lastRead?: number,
    sort?: string,
    sortedLastReadValue?: string,
    searchValue?: string,
    comparisonOperator?: COMPARE_OPERATOR | '',
    searchFields?: string[],
    showAll?: boolean,
    showInvalid?: boolean,
  ): IAccountsPaginationProps {
    // Input validation
    if (!fiscalYear) {
      throw new Error('fiscalYear is required');
    }

    if (!pageSize || pageSize <= 0) {
      throw new Error('pageSize must be a positive number');
    }

    if (!Array.isArray(filters)) {
      throw new Error('filters must be an array');
    }

    return {
      fiscalYear: fiscalYear.trim(),
      pageSize: Math.max(1, pageSize),
      filters,
      lastRead: lastRead ?? 0,
      sort: sort ?? null,
      sortedLastReadValue: sortedLastReadValue ?? null,
      searchValue: searchValue ?? null,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      comparisonOperator: comparisonOperator ?? null,
      searchFields: searchFields ?? [],
      showAll: showAll ?? false,
      showInvalid: showInvalid ?? false,
    };
  }

  public static buildAccountsAggregatesProps(
    fiscalYear: string,
    filters: IFilter[],
    searchValue?: string,
    comparisonOperator?: COMPARE_OPERATOR | '',
    searchFields?: string[],
    showAll?: boolean,
    showInvalid?: boolean,
  ): IAccountsAggregationProps {
    // Input validation
    if (!fiscalYear) {
      throw new Error('fiscalYear is required');
    }

    if (!Array.isArray(filters)) {
      throw new Error('filters must be an array');
    }

    return {
      fiscalYear: fiscalYear.trim(),
      filters,
      searchValue: searchValue ?? null,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      comparisonOperator: comparisonOperator ?? null,
      searchFields: searchFields ?? [],
      showAll: showAll ?? false,
      showInvalid: showInvalid ?? false,
    };
  }

  private static getLastDayOfFiscalYear(): Date {
    const now = DateTime.now();
    const fiscalYearStartMonth = 10;
    return DateTime.now().month < 10
      ? DateTime.fromObject({ year: now.year, month: fiscalYearStartMonth - 1, day: 30 }).toJSDate()
      : DateTime.fromObject({ year: now.year + 1, month: fiscalYearStartMonth - 1, day: 30 }).toJSDate();
  }

  /**
   * Prepares an account object for saving by ensuring specific fields are set to empty strings if they are null
   * (very important for settlement run and filtering in account view)
   * @param account The account object to prepare
   * @returns The prepared account object with appropriate empty strings (very important for settlement run and filtering in account view)
   */
  public static prepareAccountForSaveAndSetNullFieldsToEmptyString(account: IAccount): IAccount {
    // Fields that should be empty strings instead of null
    const emptyStringFields = [
      'accountInternalPSP',
      'bgbk',
      'billingPSPBZPO',
      'customerOrder',
      'orderHPosRK',
      'orderHPosOC',
      'orderMainPos',
      'projectOrgID',
      'sapSDOrder',
      'toGeneralLedgerAccount',
    ];

    const preparedAccount = { ...account };

    // Ensure specified fields are set to empty string if null
    emptyStringFields.forEach(field => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      if (preparedAccount[field] === null) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        preparedAccount[field] = '';
      }
    });

    return preparedAccount;
  }
}
